import {
  loadStripe,
  type PaymentIntent,
  type Stripe,
  type StripeCardElement,
  type StripeElements,
} from '@stripe/stripe-js'
import { createModal } from '/@/core/elements/modal'
import { useMutation } from '/@/core/graphql'
import { gql } from '@apollo/client/core'
import { useConfig } from '/@/core/config'
import { useRollbar } from '../rollbar'
import { messageResultFragment } from '/@/pages/Signup/fragments'
import type { StripeCustomer } from '/@/common/accounts/types'
import { stripeCustomerFragment } from '/@/common/accounts/fragments'

interface ConfirmPaymentParams {
  elements: StripeElements
  returnUrl: string
}

interface ConfirmPaymentMethodParams {
  elements: StripeElements
  returnUrl: string
  stripeCustomerId?: string | null
}

interface ConfirmSetupIntentPaymentMethodParams {
  setupIntentClientSecret: string
  stripeCustomerId?: string | null
}

interface ConfirmCardPaymentParams {
  paymentId: string
  card: StripeCardElement
}

interface ConfirmCardPaymentMethodParams {
  secretKey: string
  card: StripeCardElement
  stripeCustomerId?: string | null
}

interface MetaData {
  key: string
  value: string
}

interface PayParams {
  amount: number
  description: string
  metaData?: MetaData[]
  serviceId?: string
}

interface PurchaseItem {
  title: string
  amountCents: number | null
}

interface QuickPayParams {
  title?: string
  description?: string
  paymentDescription?: string
  items: PurchaseItem[]
  serviceId?: string
  withNote?: boolean
  noteLabel?: string
  ctaLabel?: string
}

interface ClientInfo {
  firstName: string
  lastName: string
  email: string
}

interface SuccessData {
  paymentIntent?: PaymentIntent
  clientInfo?: ClientInfo
  giftedClientInfo?: ClientInfo
  note?: string
}

interface AddressInput {
  line1?: string | null
  line2?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  postalCode?: string | null
}

export interface UpdateCustomerAddressResult {
  status: number
  stripeCustomer?: StripeCustomer
  message?: {
    status: number
    text: string
  }
}

let cachedStripe: unknown
export function usePaymentsApi() {
  const config = useConfig()
  const modal = usePaymentsModal()
  const rollbar = useRollbar()

  async function getStripe() {
    if (!cachedStripe) {
      cachedStripe = await loadStripe(config.apiKeys.stripe)
    }
    return cachedStripe as Stripe
  }

  const { mutate: getPaymentInfo } = useMutation<{
    payment: {
      create: {
        clientSecretKey?: string
        defaultPaymentSource?: string
        status: number
      }
    }
  }>(gql`
    mutation getStripePaymentInfo($input: PaymentCreateInput!) {
      payment {
        create(input: $input) {
          clientSecretKey
          defaultPaymentSource
          status
        }
      }
    }
  `)

  async function getPaymentId(input: PayParams) {
    const response = await getPaymentInfo({ input })
    const info = response?.data?.payment.create
    if (!info?.clientSecretKey) {
      rollbar.error('getPaymentId error', {
        status: info?.status,
      })
      throw Error('getPaymentId error')
    }
    return info?.clientSecretKey as string
  }

  async function quickPay({
    title,
    description,
    paymentDescription,
    items,
    serviceId,
    withNote,
    noteLabel,
    ctaLabel,
  }: QuickPayParams) {
    const amount = items.reduce(
      (sum, it) => (it.amountCents == null ? sum : sum + it.amountCents),
      0,
    )
    return new Promise<SuccessData>((resolve, reject) => {
      modal.show({
        title,
        description,
        items,
        withNote,
        noteLabel,
        ctaLabel,
        giftEnabled: Boolean(serviceId) && amount > 0,
        paymentId: amount
          ? getPaymentId({
              serviceId,
              amount,
              description: paymentDescription ?? '',
            })
          : Promise.resolve(null),
        onSuccess: resolve,
        onError: reject,
      })
    })
  }

  async function confirmPayment(
    { elements, returnUrl }: ConfirmPaymentParams,
    onSuccess: () => void,
    onError: (errorMessage?: string) => void,
  ) {
    const stripe = await getStripe()

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
    })

    if (error) {
      onError(error.message)
    } else {
      onSuccess()
    }
  }

  async function confirmPaymentMethod({
    elements,
    returnUrl,
    stripeCustomerId,
  }: ConfirmPaymentMethodParams) {
    const stripe = await getStripe()
    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
      redirect: 'if_required',
    })
    if (error) {
      throw error
    }

    return await setPaymentMethod({
      paymentMethodId: setupIntent?.payment_method,
      stripeCustomerId,
    })
  }

  async function confirmSetupIntentPaymentMethod({
    setupIntentClientSecret,
    stripeCustomerId,
  }: ConfirmSetupIntentPaymentMethodParams) {
    const stripe = await getStripe()
    const { setupIntent, error } = await stripe.retrieveSetupIntent(
      setupIntentClientSecret,
    )
    if (error) {
      throw error
    }
    await setPaymentMethod({
      paymentMethodId: setupIntent?.payment_method,
      stripeCustomerId,
    })

    return
  }

  async function confirmCardPayment({
    card,
    paymentId,
  }: ConfirmCardPaymentParams) {
    const stripe = await getStripe()
    const { paymentIntent, error } = await stripe.confirmCardPayment(
      paymentId,
      {
        payment_method: {
          card,
        },
      },
    )
    if (error) {
      throw error
    }

    return paymentIntent
  }

  const { mutate: setPaymentMethod } = useMutation<{
    user?: {
      setDefaultPaymentMethod?: {
        stripeCustomer?: StripeCustomer
        message?: {
          text: string
        }
      }
    }
  }>(gql`
    ${stripeCustomerFragment}
    mutation setDefaultPaymentMethod(
      $paymentMethodId: String!
      $stripeCustomerId: String
    ) {
      user {
        setDefaultPaymentMethod(
          paymentMethodId: $paymentMethodId
          stripeCustomerId: $stripeCustomerId
        ) {
          status
          stripeCustomerId
          stripeCustomer {
            ...StripeCustomerProps
          }
          message {
            text
          }
        }
      }
    }
  `)

  const { mutate: updateCustomerAddress } = useMutation<{
    user: { updateCustomerAddress: UpdateCustomerAddressResult }
  }>(gql`
    ${stripeCustomerFragment}
    ${messageResultFragment}
    mutation updateCustomerAddress(
      $stripeCustomerId: String
      $address: AddressInput!
    ) {
      user {
        updateCustomerAddress(
          stripeCustomerId: $stripeCustomerId
          address: $address
        ) {
          stripeCustomer {
            ...StripeCustomerProps
          }
          status
          message {
            ...MessageResultProps
          }
        }
      }
    }
  `)

  async function confirmCardPaymentMethod({
    card,
    secretKey,
    stripeCustomerId,
  }: ConfirmCardPaymentMethodParams) {
    const stripe = await getStripe()
    const { setupIntent, error } = await stripe.confirmCardSetup(secretKey, {
      payment_method: {
        card,
      },
    })
    if (error) {
      throw error
    }

    await setPaymentMethod({
      paymentMethodId: setupIntent?.payment_method,
      stripeCustomerId,
    })

    return
  }

  return {
    async getElements(options: Record<string, unknown> = {}) {
      return (await getStripe()).elements(options)
    },
    quickPay,
    confirmPayment,
    confirmPaymentMethod,
    confirmSetupIntentPaymentMethod,
    confirmCardPayment,
    confirmCardPaymentMethod,
    async updateCustomerAddress(
      stripeCustomerId: string,
      address: AddressInput,
    ) {
      return await updateCustomerAddress({ stripeCustomerId, address })
    },
  }
}

export interface PaymentsModalData {
  title?: string
  description?: string
  items: PurchaseItem[]
  paymentId: Promise<string | null>
  withNote?: boolean
  noteLabel?: string
  ctaLabel?: string
  giftEnabled?: boolean
  onSuccess?: (data: SuccessData) => unknown
  onError?: (e?: Error) => unknown
}

const { api: paymentsModalApi, component: PaymentsModal } =
  createModal<PaymentsModalData>(
    'PaymentsModal',
    () => import('./PaymentsModal.vue'),
    {
      title: '',
      description: '',
      items: [],
      withNote: false,
      noteLabel: 'note',
      ctaLabel: 'pay',
      giftEnabled: false,
      paymentId: Promise.resolve(''),
    },
  )

export interface CollectPaymentMethodModalData {
  onSuccess?: () => unknown
  onError?: (e?: Error) => unknown
}
const {
  api: collectPaymentMethodModalApi,
  component: CollectPaymentMethodModal,
} = createModal<CollectPaymentMethodModalData>(
  'CollectPaymentMethodModal',
  () => import('./CollectPaymentMethodModal.vue'),
  {},
)

export const usePaymentsModal = paymentsModalApi
export const useCollectPaymentMethodModal = collectPaymentMethodModalApi

export { PaymentsModal, CollectPaymentMethodModal }
